/**
 * In-app chat widget (M5.3).
 *
 * Floating bottom-right button → opens a panel that lets the user open
 * tickets and view replies. All requests go through compta's
 * /api/chat/* proxy (which forwards to nexus-agents). The widget never
 * sees X-Internal-API-Key.
 *
 * Polling: 5s while a ticket is open. Pending agent drafts (visible_to_client
 * = false) never reach the user — the proxy filters them out.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Plus, X, Send, Star, ArrowLeft, RefreshCw } from 'lucide-react';

// SIRH already exposes /api/chat for the legacy AI assistant — our M5
// support tickets are mounted under /api/support to avoid the conflict.
const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/support';
const POLL_INTERVAL_MS = 5000;

function statusLabel(status) {
  switch (status) {
    case 'new': return 'Nouveau';
    case 'processing': return 'En cours…';
    case 'pending_approval': return 'En attente de validation';
    case 'replied': return 'Répondu';
    case 'escalated': return 'Escaladé';
    case 'closed': return 'Clos';
    default: return status;
  }
}

function statusColor(status) {
  switch (status) {
    case 'pending_approval': return 'bg-amber-100 text-amber-800';
    case 'replied': return 'bg-emerald-100 text-emerald-800';
    case 'escalated': return 'bg-red-100 text-red-800';
    case 'closed': return 'bg-gray-100 text-gray-700';
    default: return 'bg-blue-100 text-blue-800';
  }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'new' | 'thread'
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [activeId, setActiveId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [draft, setDraft] = useState('');

  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const messagesEndRef = useRef(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_BASE}/my-tickets`);
      setTickets(r.data?.tickets || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (ticketId) => {
    try {
      const r = await axios.get(`${API_BASE}/${ticketId}`);
      setActiveDetail(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (view === 'list') loadTickets();
  }, [open, view, loadTickets]);

  // Poll detail every 5s while a thread is open
  useEffect(() => {
    if (view !== 'thread' || !activeId) return;
    loadDetail(activeId);
    const id = setInterval(() => loadDetail(activeId), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [view, activeId, loadDetail]);

  // Auto-scroll to last message on update
  useEffect(() => {
    if (view === 'thread' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeDetail, view]);

  const openTicket = async () => {
    if (!newSubject.trim() || !newMessage.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(API_BASE, {
        subject: newSubject.trim(),
        message: newMessage.trim(),
      });
      setNewSubject('');
      setNewMessage('');
      setActiveId(r.data.id);
      setActiveDetail(r.data);
      setView('thread');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = async () => {
    if (!activeId || !draft.trim()) return;
    const message = draft.trim();
    setDraft('');
    try {
      const r = await axios.post(`${API_BASE}/${activeId}/messages`, { message });
      setActiveDetail(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur');
      setDraft(message); // restore
    }
  };

  const submitRating = async (rating) => {
    if (!activeId) return;
    try {
      const r = await axios.post(`${API_BASE}/${activeId}/rating`, { rating });
      setActiveDetail(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur');
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir le chat support"
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 z-50 flex h-[560px] w-[380px] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-gray-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          {view === 'thread' || view === 'new' ? (
            <button
              type="button"
              onClick={() => { setView('list'); setActiveId(null); setActiveDetail(null); }}
              aria-label="Retour"
              className="rounded p-1 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}
          <span className="font-semibold text-gray-800">Support Nexus</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Fermer le chat support"
          className="rounded p-1 hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && (
        <div className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          {String(error)}
        </div>
      )}

      {/* Body */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {view === 'list' && (
          <>
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-xs text-gray-500">Mes conversations</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={loadTickets}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                  aria-label="Rafraîchir"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={() => setView('new')}
                  className="flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                >
                  <Plus className="h-3 w-3" /> Nouveau
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {tickets.length === 0 && !loading ? (
                <div className="p-6 text-center text-sm text-gray-500">
                  Aucune conversation.<br />Démarrez-en une.
                </div>
              ) : (
                <ul className="divide-y">
                  {tickets.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => { setActiveId(t.id); setView('thread'); }}
                        className="flex w-full flex-col gap-1 px-3 py-2 text-left hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-gray-900">
                            {t.subject || '(sans sujet)'}
                          </span>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${statusColor(t.status)}`}>
                            {statusLabel(t.status)}
                          </span>
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : ''}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {view === 'new' && (
          <div className="flex flex-1 flex-col gap-3 p-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">Sujet</label>
              <input
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                maxLength={200}
                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Ex : Erreur sur ma facture #123"
              />
            </div>
            <div className="flex flex-1 flex-col">
              <label className="mb-1 block text-xs font-semibold text-gray-600">Message</label>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={6}
                maxLength={8000}
                className="flex-1 resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Décrivez votre demande…"
              />
            </div>
            <button
              type="button"
              onClick={openTicket}
              disabled={submitting || !newSubject.trim() || !newMessage.trim()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        )}

        {view === 'thread' && (
          <>
            <div className="border-b px-3 py-2">
              <div className="text-sm font-semibold text-gray-900">
                {activeDetail?.subject || '…'}
              </div>
              {activeDetail && (
                <div className="mt-1 flex items-center gap-2 text-[11px]">
                  <span className={`rounded-full px-2 py-0.5 ${statusColor(activeDetail.status)}`}>
                    {statusLabel(activeDetail.status)}
                  </span>
                  {activeDetail.status === 'pending_approval' && (
                    <span className="text-gray-500">— l&apos;équipe vérifie une réponse pour vous</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto p-3">
              {(activeDetail?.messages || []).map((m) => (
                <Bubble key={m.id} sender={m.sender} content={m.content} createdAt={m.created_at} />
              ))}
              {activeDetail?.messages?.length === 0 && (
                <div className="text-center text-xs text-gray-400">Aucun message visible pour l&apos;instant.</div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Rating zone if closed */}
            {activeDetail?.status === 'closed' && !activeDetail.rating && (
              <div className="border-t px-3 py-2 text-center">
                <div className="text-xs text-gray-600">Notez cette conversation :</div>
                <div className="mt-1 flex justify-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => submitRating(n)}
                      className="text-amber-500 hover:scale-110"
                      aria-label={`Donner ${n} étoile(s)`}
                    >
                      <Star className="h-5 w-5" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Composer */}
            {activeDetail?.status !== 'closed' && (
              <div className="border-t p-2">
                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    maxLength={8000}
                    className="flex-1 resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Écrivez votre message…"
                  />
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={!draft.trim()}
                    className="flex h-9 items-center gap-1 rounded-md bg-blue-600 px-3 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                    aria-label="Envoyer"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Bubble({ sender, content, createdAt }) {
  const isClient = sender === 'client';
  return (
    <div className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
          isClient
            ? 'rounded-br-none bg-blue-600 text-white'
            : 'rounded-bl-none bg-gray-100 text-gray-800'
        }`}
      >
        {content}
        <div className={`mt-1 text-[10px] ${isClient ? 'text-blue-100' : 'text-gray-500'}`}>
          {new Date(createdAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
